import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import { DASHBOARD_KEY } from '@/hooks/useDashboard';
import { ANALYTICS_KEY } from '@/features/analytics/useIssuingAnalytics';
import type {
  AdjustmentPayload,
  DeleteMovementResult,
  ExpiringBatch,
  ExpiryStatusFilter,
  IssuePayload,
  Item,
  ItemDetail,
  ItemsPage,
  ItemType,
  StockActivityFilters,
  StockInPayload,
  StockMovement,
  StockMovementLogRow,
  StockMovementsPage,
  StockStatusFilter,
  UpdateMovementPayload,
} from '@/types/api';

const ITEMS_KEY = ['inventory', 'items'] as const;
const EXPIRING_KEY = ['inventory', 'expiring'] as const;
export const ACTIVITY_KEY = ['inventory', 'activity'] as const;

/**
 * Everything a stock write can invalidate.
 *
 * A correction moves a balance, so it can change the stock list, the expiring
 * set, the ledger itself, the dashboard totals and the issuing analytics. The
 * existing stock mutations predate the last two keys and don't invalidate them
 * — a gap, but not one to widen by copying it here.
 */
function invalidateStockViews(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ITEMS_KEY });
  qc.invalidateQueries({ queryKey: EXPIRING_KEY });
  qc.invalidateQueries({ queryKey: ACTIVITY_KEY });
  qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
  qc.invalidateQueries({ queryKey: ANALYTICS_KEY });
}

interface ItemsSearchParams {
  query?: string;
  type?: ItemType;
  limit?: number;
  /** When false, the query stays idle (no fetch). Default: true. */
  enabled?: boolean;
}

/**
 * Characters typed before an item search is worth running.
 *
 * Here rather than in each field, because it is a property of the search — one
 * or two letters match most of the catalogue, so the request costs a round trip
 * to return a list nobody can use. It was written out separately in four
 * pickers, which is three places for it to be changed to something else.
 */
export const SEARCH_MIN_CHARS = 2;

/**
 * Type-ahead / catalogue search for items in the active clinic.
 *
 * Backend trims and lowercases nothing — it ILIKE-matches brand and generic
 * names. We pass the raw query straight through; the caller is responsible
 * for any debouncing (see useDebouncedItemsSearch below).
 */
export function useItemsSearch(params: ItemsSearchParams = {}) {
  const { query, type, limit = 8, enabled = true } = params;
  const trimmed = query?.trim() ?? '';
  return useQuery({
    queryKey: [...ITEMS_KEY, 'search', { query: trimmed, type, limit }],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (trimmed) search.set('query', trimmed);
      if (type) search.set('type', type);
      search.set('limit', String(limit));
      const page = await clinicApi.get<ItemsPage>(`items?${search.toString()}`);
      return page.items;
    },
    enabled,
    // Empty-query searches return up to `limit` items in brand order — useful
    // as a fallback list when the user opens the dropdown without typing.
    staleTime: 30_000,
  });
}

interface StockListParams {
  query?: string;
  type?: ItemType;
  stockStatus?: StockStatusFilter;
  expiryStatus?: ExpiryStatusFilter;
  expiringWithinDays?: number;
  /** 1-based page index. Defaults to 1. */
  page?: number;
  /** Rows per page. Defaults to 25, hard-capped server-side at 200. */
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Stock Status list query — paginated `GET /api/items` for the overview
 * screen. Returns `{ items, total, limit, offset }` so the table can render a
 * "showing X–Y of Z" footer without a second count call. Uses
 * `keepPreviousData` so paging doesn't flash the empty state between requests.
 */
export function useStockList(params: StockListParams = {}) {
  const {
    query,
    type,
    stockStatus,
    expiryStatus,
    expiringWithinDays,
    page = 1,
    pageSize = 25,
    enabled = true,
  } = params;
  const trimmed = query?.trim() ?? '';
  const offset = Math.max(0, (page - 1) * pageSize);
  return useQuery({
    queryKey: [
      ...ITEMS_KEY,
      'stock-list',
      { query: trimmed, type, stockStatus, expiryStatus, expiringWithinDays, pageSize, offset },
    ],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (trimmed) search.set('query', trimmed);
      if (type) search.set('type', type);
      if (stockStatus) search.set('stockStatus', stockStatus);
      if (expiryStatus) search.set('expiryStatus', expiryStatus);
      if (expiringWithinDays !== undefined)
        search.set('expiringWithinDays', String(expiringWithinDays));
      search.set('limit', String(pageSize));
      search.set('offset', String(offset));
      return clinicApi.get<ItemsPage>(`items?${search.toString()}`);
    },
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Submit a stock-in batch. The backend resolves each entry as either an
 * existing itemId or an inline newItem and writes everything in one
 * transaction (see stockService.stockIn). On success we invalidate the items
 * caches so any list re-fetches.
 */
export function useStockIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StockInPayload) =>
      clinicApi.post<StockMovement[]>('stock/stock-in', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ITEMS_KEY });
      qc.invalidateQueries({ queryKey: ACTIVITY_KEY });
    },
  });
}

/**
 * Detail for a single item — item record + FEFO-ordered batches with balances
 * + totalStock. The Issue screen uses this to populate the batch picker when
 * an item has multiple stocked batches.
 */
export function useItemDetail(itemId: string | null, opts: { enabled?: boolean } = {}) {
  const enabled = (opts.enabled ?? true) && !!itemId;
  return useQuery({
    queryKey: [...ITEMS_KEY, 'detail', itemId],
    queryFn: () => clinicApi.get<ItemDetail>(`items/${itemId}`),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * Exact-match barcode lookup. Resolves to the same `ItemDetail` shape as
 * useItemDetail so the scanner flow can apply the same batch-selection logic
 * (auto-attach single batch, or open the FEFO override popover).
 */
export function useItemByBarcode(barcode: string | null, opts: { enabled?: boolean } = {}) {
  const code = barcode?.trim() ?? '';
  const enabled = (opts.enabled ?? true) && code.length > 0;
  return useQuery({
    queryKey: [...ITEMS_KEY, 'by-barcode', code],
    queryFn: () => clinicApi.get<ItemDetail>(`items/by-barcode/${encodeURIComponent(code)}`),
    enabled,
    // Stock changes invalidate ITEMS_KEY, so this re-fetches as needed.
    staleTime: 15_000,
    // Don't retry on 404 — "unknown barcode" is a meaningful, terminal answer.
    retry: false,
  });
}

/**
 * Submit a batched issue. Server validates each entry, holds SELECT ... FOR
 * UPDATE on each touched balance row, and rolls back the entire submission
 * if any entry would oversell — so the UI gets one coherent success or one
 * coherent failure, never a partial write.
 */
export function useStockIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: IssuePayload) =>
      clinicApi.post<StockMovement[]>('stock/issue', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ITEMS_KEY });
      qc.invalidateQueries({ queryKey: EXPIRING_KEY });
      qc.invalidateQueries({ queryKey: ACTIVITY_KEY });
    },
  });
}

/**
 * Submit a batched adjustment (discard / expired / damaged / lost). Same
 * all-or-nothing transaction semantics as Issue.
 */
export function useStockAdjust() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdjustmentPayload) =>
      clinicApi.post<StockMovement[]>('stock/adjustment', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ITEMS_KEY });
      qc.invalidateQueries({ queryKey: EXPIRING_KEY });
      qc.invalidateQueries({ queryKey: ACTIVITY_KEY });
    },
  });
}

interface StockActivityParams extends StockActivityFilters {
  /** 1-based page index. Defaults to 1. */
  page?: number;
  /** Rows per page. Defaults to 25, hard-capped server-side at 200. */
  pageSize?: number;
}

/**
 * The clinic's stock ledger, newest first — one row per movement.
 *
 * `keepPreviousData` so paging and filtering don't flash the empty state, the
 * same as the stock list. Each row carries `editable`, computed server-side
 * against the whole batch rather than the filtered page, so the buttons the
 * page enables always match what the API will accept.
 */
export function useStockActivity(params: StockActivityParams = {}) {
  const { kind, itemId, query, from, to, page = 1, pageSize = 25 } = params;
  const trimmed = query?.trim() ?? '';
  const offset = Math.max(0, (page - 1) * pageSize);
  return useQuery({
    queryKey: [...ACTIVITY_KEY, { kind, itemId, query: trimmed, from, to, pageSize, offset }],
    queryFn: () => {
      const search = new URLSearchParams();
      if (kind) search.set('kind', kind);
      if (itemId) search.set('itemId', itemId);
      if (trimmed) search.set('query', trimmed);
      if (from) search.set('from', from);
      if (to) search.set('to', to);
      search.set('limit', String(pageSize));
      search.set('offset', String(offset));
      return clinicApi.get<StockMovementsPage>(`stock/movements?${search.toString()}`);
    },
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Correct a movement. Only the latest movement for a batch may be changed; the
 * server returns 409 CONFLICT otherwise, which the dialog surfaces verbatim
 * because the reason ("someone else just issued from this batch") is the useful
 * part.
 */
export function useUpdateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateMovementPayload }) =>
      clinicApi.patch<StockMovementLogRow>(`stock/movements/${id}`, patch),
    onSuccess: () => invalidateStockViews(qc),
  });
}

/** Delete a movement, reversing its effect on the batch balance. */
export function useDeleteMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clinicApi.delete<DeleteMovementResult>(`stock/movements/${id}`),
    onSuccess: () => invalidateStockViews(qc),
  });
}

/**
 * Batches with positive balance whose expiry falls within `withinDays` (default
 * 30) — already-expired batches always included, sorted soonest-first. Powers
 * the Adjust page's "expiring soon" affordance.
 */
export function useExpiringBatches(params: { withinDays?: number; limit?: number; enabled?: boolean } = {}) {
  const { withinDays = 30, limit = 50, enabled = true } = params;
  return useQuery({
    queryKey: [...EXPIRING_KEY, { withinDays, limit }],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('withinDays', String(withinDays));
      qs.set('limit', String(limit));
      return clinicApi.get<ExpiringBatch[]>(`batches/expiring?${qs.toString()}`);
    },
    enabled,
    staleTime: 30_000,
  });
}
